import { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import {
  apiAdminStagingRun, apiAdminStagingReport, apiAdminStagingDownloadReport, ApiRequestError,
} from '@/lib/api'
import type { StagingCaseResult, StagingRunDetail } from '@nexbit/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CopyButton } from '@/components/ui/code-block'
import { cn } from '@/lib/utils'
import {
  AlertCircle, AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, Download,
  MinusCircle, XCircle,
} from 'lucide-react'

/** One past run in full, with the report ready to hand to the agent. */

const STATUS_META: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
  passed: { label: 'ผ่าน', icon: <CheckCircle2 className="h-4 w-4" />, cls: 'text-success' },
  failed: { label: 'ไม่ผ่าน', icon: <XCircle className="h-4 w-4" />, cls: 'text-destructive' },
  error: { label: 'ผิดพลาด', icon: <XCircle className="h-4 w-4" />, cls: 'text-destructive' },
  warn: { label: 'ข้อสังเกต', icon: <AlertTriangle className="h-4 w-4" />, cls: 'text-warning' },
  skipped: { label: 'ข้าม', icon: <MinusCircle className="h-4 w-4" />, cls: 'text-muted-foreground' },
}

function CaseDetail({ c, runId }: { c: StagingCaseResult; runId: string }) {
  const meta = STATUS_META[c.status] ?? STATUS_META['skipped']!
  const [caseReport, setCaseReport] = useState<string | null>(null)

  return (
    <details
      className="group rounded-[var(--radius-sm)] border border-border bg-card"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && !caseReport) {
          void apiAdminStagingReport(runId, c.caseKey)
            .then((r) => setCaseReport(r.markdown))
            .catch(() => setCaseReport(null))
        }
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2.5 px-3 py-2.5 text-sm">
        <span className={cn('shrink-0', meta.cls)}>{meta.icon}</span>
        <span className="min-w-0 flex-1 truncate">{c.titleTh}</span>
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{c.caseKey}</span>
        <span className={cn('shrink-0 text-xs font-semibold', meta.cls)}>{meta.label}</span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>

      <div className="space-y-3 border-t border-border px-3 py-3 text-xs">
        {(c.failureTh || c.hintTh) && (
          <div className={cn('space-y-1 rounded-[var(--radius-sm)] p-2.5',
            c.status === 'warn' ? 'bg-warning/10' : 'bg-destructive/5')}>
            {c.failureTh && <p className="leading-relaxed">❌ {c.failureTh}</p>}
            {c.hintTh && <p className="leading-relaxed text-muted-foreground">💡 {c.hintTh}</p>}
          </div>
        )}

        {c.assertions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">สิ่งที่ตรวจ</th>
                  <th className="py-1 pr-3 font-medium">ต้องเป็น</th>
                  <th className="py-1 font-medium">ที่ได้จริง</th>
                </tr>
              </thead>
              <tbody>
                {c.assertions.map((a, i) => (
                  <tr key={i} className="border-t border-border/60">
                    <td className="py-1 pr-3">{a.name}</td>
                    <td className="py-1 pr-3 font-mono">{a.want}</td>
                    <td className={cn('py-1 font-mono', a.ok ? 'text-success' : 'font-semibold text-destructive')}>{a.got}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {c.httpMethod && (
          <div className="grid gap-2 lg:grid-cols-2">
            <div>
              <p className="mb-1 font-mono text-[10px] text-muted-foreground">Request · {c.httpMethod} {c.httpPath}</p>
              <pre className="max-h-56 overflow-auto rounded-[var(--radius-sm)] bg-muted p-2 font-mono text-[10px] leading-relaxed">
                {JSON.stringify(c.requestBody, null, 2)}
              </pre>
            </div>
            <div>
              <p className="mb-1 font-mono text-[10px] text-muted-foreground">Response · {c.httpStatus ?? 'ไม่มีการตอบกลับ'}</p>
              <pre className="max-h-56 overflow-auto rounded-[var(--radius-sm)] bg-muted p-2 font-mono text-[10px] leading-relaxed">
                {JSON.stringify(c.responseBody, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {caseReport && (
          <div className="flex justify-end">
            <CopyButton text={caseReport} label="คัดลอกเฉพาะเคสนี้" />
          </div>
        )}
      </div>
    </details>
  )
}

export default function StagingRunDetailPage() {
  const { runId } = useParams<{ runId: string }>()
  const [detail, setDetail] = useState<StagingRunDetail | null>(null)
  const [report, setReport] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!runId) return
    setLoading(true)
    setError(null)
    try {
      const d = await apiAdminStagingRun(runId)
      setDetail(d)
      try {
        setReport((await apiAdminStagingReport(runId)).markdown)
      } catch { /* the results below still stand without the copy text */ }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดผลทดสอบไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [runId])

  useEffect(() => { void load() }, [load])

  return (
    <div className="max-w-5xl p-4 md:p-6">
      <Button asChild variant="ghost" size="sm" className="mb-3 gap-1.5 px-2">
        <Link to="/admin/staging/runs">
          <ArrowLeft className="h-3.5 w-3.5" />
          กลับหน้าประวัติ
        </Link>
      </Button>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : error ? (
        <div role="alert" className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : detail ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold tracking-tight">{detail.run.partnerCode}</h1>
            <Badge variant="outline" className="border-warning/50 bg-warning/10 text-warning">STAGING</Badge>
            <span className="text-sm text-muted-foreground">{detail.run.partnerName}</span>
          </div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {format(new Date(detail.run.startedAt), 'dd/MM/yyyy HH:mm:ss')}
            {detail.run.targetHost ? ` · ${detail.run.targetHost}` : ''}
            {detail.run.testMember ? ` · สมาชิก ${detail.run.testMember}` : ''}
          </p>

          <section className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
              <span className="text-sm font-semibold">
                ผ่าน {detail.run.passedCount} · ไม่ผ่าน {detail.run.failedCount} · ข้าม {detail.run.skippedCount}
              </span>
              {detail.run.durationMs !== null && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  {(detail.run.durationMs / 1000).toFixed(1)} วินาที
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {report && <CopyButton text={report} label="คัดลอกผลให้เอเจนต์" />}
                <Button variant="outline" size="sm" className="gap-1.5"
                  onClick={() => void apiAdminStagingDownloadReport(detail.run.id)}>
                  <Download className="h-3.5 w-3.5" />
                  ดาวน์โหลด
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 p-3">
              {detail.cases.map((c) => <CaseDetail key={c.seq} c={c} runId={detail.run.id} />)}
            </div>
          </section>
        </>
      ) : null}
    </div>
  )
}
